import React, { FC, useEffect, useState } from 'react';

import Dialog from 'components/Dialog/Dialog';

import { useMapContextRedistributor } from 'app/contexts/MapContextRedistributor/mapContextRedistributorProvider';
import { useMapVariant } from 'app/contexts/mapVariantProvider';

import { HierarchyType } from 'app/models';

import block from 'utils/bem-css-modules';
import { createScopedSessionStorage } from 'utils/helpers/sessionStorageHelpers';
import { formatMessage, formatNumber } from 'utils/messages/utils';

import { AccountPinWarning } from './hooks/useCustomerAggregatesFeatures';
import style from './TerritoryMapV3.module.pcss';
const b = block(style);

export interface AccountPinWarningDialogsProps {
  warning: AccountPinWarning;
}

const AccountPinWarningDialogs: FC<AccountPinWarningDialogsProps> = ({ warning }) => {
  const { primaryHierarchy } = useMapContextRedistributor();

  if (!warning) return null;

  switch (primaryHierarchy) {
    case HierarchyType.GeographicTerritoryHierarchy:
      return <GeoFirstPinWarningDialog warning={warning} />;
    case HierarchyType.CustomerAccountHierarchy:
      return <AccountFirstPinWarningDialog warning={warning} />;
    default:
      console.error(`Unexpected primary hierarchy ${primaryHierarchy}`);
      return null;
  }
};

const isDismissedStorage = createScopedSessionStorage<boolean>('DISMISS_GEO_FIRST_ACCOUNT_PIN_WARNING');
const GeoFirstPinWarningDialog: FC<AccountPinWarningDialogsProps> = ({ warning }) => {
  const [isOpen, setIsOpen] = useState(() => !isDismissedStorage.get());

  const handleSubmit = () => {
    setIsOpen(false);
    isDismissedStorage.set(true);
  };

  return (
    <Dialog
      title={formatMessage('ACCOUNT_PIN_MAX_GEO_FIRST_TITLE')}
      confirmButtonText={formatMessage('ACCOUNT_PIN_MAX_GEO_FIRST_CONFIRM')}
      isOpen={isOpen}
      onSubmit={handleSubmit}
      showCancel={false}
      size="small"
      bodyMinHeight={0}
      data-testid="account-pin-warning-dialog-geo-first"
    >
      <WarningDialogDescription maxCount={warning.maxCount} messageKey="ACCOUNT_PIN_MAX_GEO_FIRST_DESCRIPTION" />
    </Dialog>
  );
};

const AccountFirstPinWarningDialog: FC<AccountPinWarningDialogsProps> = ({ warning }) => {
  const { isEmbedded } = useMapContextRedistributor();
  const { isDataTrayMapOpen, isMultiMapOpen, isPreviewMapOpen, setIsDataTrayMapOpen } = useMapVariant();

  useEffect(() => {
    if (isPreviewMapOpen || isMultiMapOpen || isEmbedded) {
      console.error('Account first map opened by unsupported map variant', {
        isPreviewMapOpen,
        isMultiMapOpen,
        isEmbedded
      });
    }
  }, [isPreviewMapOpen, isMultiMapOpen, isEmbedded]);

  return (
    <Dialog
      title={formatMessage('MAP_LOADING_WARNING_TITLE')}
      confirmButtonText={formatMessage('BACK_TO_GRID_VIEW')}
      isOpen
      showCancel={false}
      onSubmit={() => setIsDataTrayMapOpen(false)}
      showConfirm={isDataTrayMapOpen}
      size="small"
      bodyMinHeight={0}
      data-testid="account-pin-warning-dialog-account-first"
    >
      <WarningDialogDescription
        maxCount={warning.maxCount}
        messageKey={warning.kind === 'Error' ? 'ACCOUNT_PIN_ERROR_WARNING' : 'ACCOUNT_PIN_MAX_WARNING'}
      />
    </Dialog>
  );
};

type DescriptionMessageKey =
  | 'ACCOUNT_PIN_MAX_GEO_FIRST_DESCRIPTION'
  | 'ACCOUNT_PIN_ERROR_WARNING'
  | 'ACCOUNT_PIN_MAX_WARNING';

const WarningDialogDescription: FC<{ maxCount: number; messageKey: DescriptionMessageKey }> = ({
  maxCount,
  messageKey
}) => {
  return (
    <p
      className={b('accountPinWarningDialogsDescription')}
      data-testid="account-pin-warning-dialog-description"
      data-max-count={maxCount}
    >
      {formatMessage(messageKey, {
        maxCount: formatNumber(maxCount)
      })}
    </p>
  );
};

export default AccountPinWarningDialogs;
